import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, RedirectCommand, Router } from '@angular/router';

import { map, switchMap, take } from 'rxjs';

import { TicketsDataService, TicketService } from '@core/ask-hr-tickets-logic';
import { I18nService } from '@core/i18n-legacy';
import { type DialogConfigOptions, DialogService, DialogWidth } from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

import { TicketDialogComponent } from './ticket-dialog/ticket-dialog.component';
import {
  TicketEmptyStateComponent,
  type TicketEmptyStateData,
} from './ticket-empty-state/ticket-empty-state.component';

@Injectable({ providedIn: 'root' })
export class TicketDialogService {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private ticketsDataService = inject(TicketsDataService);
  private i18n = inject(I18nService);
  private dialogService = inject(DialogService);

  private watchOpenTicket = inject(TicketService)
    .openTicket$.pipe(injectUntilDestroyed())
    .subscribe(id => this.openTicketDialog(id, { routerStrategy: false }));

  public getTicketDialogOptions() {
    return {
      width: DialogWidth.LargeX,
    } satisfies DialogConfigOptions;
  }

  public getTicketEmptyStateData(): TicketEmptyStateData {
    return {
      icon: 'local-activity',
      title: this.i18n.translate('tlc.ask_hr.request_dialog.cant_view_title'),
      desc: this.i18n.translate('tlc.ask_hr.request_dialog.cant_view_desc'),
    };
  }

  public openTicketDialog(ticketId: number, options: { routerStrategy: boolean }): void {
    if (options.routerStrategy) {
      return this.navigateToTicket(ticketId);
    }

    this.resolveDialogData(ticketId)
      .pipe(
        take(1),
        switchMap(data => {
          const options = this.getTicketDialogOptions();
          return data
            ? this.dialogService.openDialog(TicketDialogComponent, data, options).afterClosed
            : this.dialogService.openDialog(
                TicketEmptyStateComponent,
                this.getTicketEmptyStateData(),
                options,
              ).afterClosed;
        }),
      )
      .subscribe();
  }

  public resolveDialogData(ticketId: number) {
    return this.ticketsDataService.loadTicketById(ticketId).pipe(
      take(1),
      map(({ isSuccess }) => {
        if (!isSuccess) return null;

        return { ticket: this.ticketsDataService.getTicketById(ticketId) };
      }),
    );
  }

  public getNotFoundRedirectCommand() {
    return new RedirectCommand(
      this.router.createUrlTree([
        {
          outlets: {
            primary: ['ask-hr', 'my', 'tickets'],
            dialog: ['ask-hr', 'ticket-not-found'],
          },
        },
      ]),
      { skipLocationChange: true },
    );
  }

  private navigateToTicket(ticketId: number) {
    const urlTree = this.router.createUrlTree(
      [
        {
          outlets: {
            dialog: ['ask-hr', 'ticket', ticketId],
          },
        },
      ],
      { relativeTo: this.route },
    );
    this.router.navigateByUrl(urlTree);
  }
}
